import React, { useState } from 'react';
import { Target, Award, ArrowUpRight, Play, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const About = () => {
  const { isArabic } = useLanguage();
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const content = {
    en: {
      tag: "Legacy & Vision",
      title: "Pioneering Retail Excellence Across the Kingdom.",
      descPart1: "AFAM Group was established with a refined vision to elevate retail and dining standards, contributing to the growth and modernization of Saudi Arabia’s landscape in alignment with Vision 2030.",
      statNumber: "26+",
      statText: "Years of Heritage",
      mTitle: "Strategic Vision",
      mDesc: "Precision in every blueprint and operational standard.",
      aTitle: "Elite Quality",
      aDesc: "Strict adherence to international retail and safety codes.",
      quote: "We don't just manage businesses; we build the infrastructure that powers the next generation of the Kingdom.",
      btn: "Explore Our Journey"
    },
    ar: {
      tag: "الإرث والرؤية",
      title: "ريادة التميز في قطاع التجزئة على مستوى المملكة.",
      descPart1: "تأسست مجموعة أقام برؤية متطورة للارتقاء بمعايير التجزئة والمطاعم، والمساهمة في نمو وتحديث المشهد السعودي بما يتماشى مع رؤية ٢٠٣٠.",
      statNumber: "٢٦+",
      statText: "عاماً من الإرث",
      mTitle: "رؤية استراتيجية",
      mDesc: "الدقة في كل مخطط ومعيار تشغيلي.",
      aTitle: "جودة النخبة",
      aDesc: "الالتزام الصارم بأكواد التجزئة والسلامة الدولية.",
      quote: "نحن لا ندير الأعمال فحسب؛ بل نبني البنية التحتية التي تمكن الجيل القادم للمملكة.",
      btn: "اكتشف مسيرتنا"
    }
  };

  const t = isArabic ? content.ar : content.en;

  return (
    <section id="about" className="py-32 bg-[#FDFDFD] overflow-hidden relative">
      {/* 1. Video Modal Overlay */}
      {isVideoOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-corp-blue/95 backdrop-blur-lg p-4 md:p-10">
          <button 
            onClick={() => setIsVideoOpen(false)}
            className="absolute top-10 right-10 text-white hover:text-saudi-green transition-colors"
          >
            <X size={40} />
          </button>
          <div className="w-full max-w-5xl aspect-video rounded-3xl overflow-hidden shadow-2xl border border-white/10">
            <iframe 
              width="100%" 
              height="100%" 
              src="https://www.youtube.com/embed/your-video-id?autoplay=1" 
              title="AFAM Group Corporate Video"
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}

      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className={`absolute top-20 ${isArabic ? '-right-20' : '-left-20'} w-96 h-96 bg-saudi-green/5 rounded-full blur-[100px]`}></div>
        <div className={`absolute bottom-20 ${isArabic ? '-left-20' : '-right-20'} w-96 h-96 bg-corp-blue/5 rounded-full blur-[100px]`}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`flex flex-col lg:flex-row items-center gap-20 ${isArabic ? 'lg:flex-row-reverse' : ''}`}>
          
          {/* Left Side: Modern Image Composition with Play Button */}
          <div className="relative lg:w-1/2 group">
            <div className="relative cursor-pointer" onClick={() => setIsVideoOpen(true)}>
              {/* Main Image */}
              <div className="relative z-10 rounded-[2.5rem] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-white/20">
                <img 
                  src="/aboutad.png" 
                  alt="AFAM Vision" 
                  className="w-full h-[600px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Play Button Overlay */}
                <div className="absolute inset-0 bg-corp-blue/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-2xl transform scale-75 group-hover:scale-100 transition-transform duration-500">
                    <Play size={32} className="text-saudi-green fill-saudi-green ml-1" />
                  </div>
                </div>
              </div>

              {/* Floating Stat Card */}
              <div className={`absolute -bottom-10 ${isArabic ? '-left-10' : '-right-10'} z-20 bg-white/80 backdrop-blur-xl p-8 rounded-[2rem] shadow-2xl border border-white max-w-[240px]`}>
                <div className="flex flex-col items-center text-center">
                  <span className="text-5xl font-black text-corp-blue mb-2">{t.statNumber}</span>
                  <span className="text-sm font-bold text-saudi-green uppercase tracking-tighter leading-tight">
                    {t.statText}
                  </span>
                </div>
              </div>

              {/* Decorative Frame */}
              <div className={`absolute -top-6 ${isArabic ? '-left-6' : '-right-6'} w-full h-full border-2 border-slate-100 rounded-[2.5rem] -z-10 transition-transform duration-500 group-hover:translate-x-2 group-hover:translate-y-2`}></div>
            </div>
          </div>

          {/* Right Side: Content & Typography */}
          <div className={`lg:w-1/2 space-y-10 ${isArabic ? 'text-right' : 'text-left'}`}>
            <div className="space-y-4">
              <div className={`flex items-center gap-3 ${isArabic ? 'flex-row-reverse' : ''}`}>
                <span className="w-12 h-[2px] bg-saudi-green"></span>
                <span className="text-saudi-green font-bold tracking-[0.2em] uppercase text-xs">{t.tag}</span>
              </div>
              <h3 className="text-4xl md:text-5xl font-black text-corp-blue leading-[1.15]">
                {t.title}
              </h3>
              <p className="text-gray-500 text-lg leading-relaxed max-w-xl">
                {t.descPart1}
              </p>
            </div>

            {/* Feature Bento-style Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className={`p-6 bg-slate-50/50 rounded-2xl border border-slate-100 hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 ${isArabic ? 'text-right' : 'text-left'}`}>
                <div className={`w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-saudi-green mb-4 ${isArabic ? 'ml-auto' : ''}`}>
                  <Target size={24} />
                </div>
                <h4 className="font-bold text-corp-blue text-lg mb-1">{t.mTitle}</h4>
                <p className="text-sm text-gray-500 leading-snug">{t.mDesc}</p>
              </div>

              <div className={`p-6 bg-slate-50/50 rounded-2xl border border-slate-100 hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 ${isArabic ? 'text-right' : 'text-left'}`}>
                <div className={`w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-saudi-green mb-4 ${isArabic ? 'ml-auto' : ''}`}>
                  <Award size={24} />
                </div>
                <h4 className="font-bold text-corp-blue text-lg mb-1">{t.aTitle}</h4>
                <p className="text-sm text-gray-500 leading-snug">{t.aDesc}</p>
              </div>
            </div>

            <div className="pt-6 border-t border-slate-100">
              <div className={`flex gap-4 items-start ${isArabic ? 'flex-row-reverse' : ''}`}>
                <span className="text-5xl text-saudi-green/20 font-serif leading-none mt-[-10px]">“</span>
                <p className="text-gray-400 italic text-md">
                  {t.quote}
                </p>
              </div>
              
              <button className={`mt-8 flex items-center gap-2 px-8 py-4 bg-corp-blue text-white rounded-full font-bold hover:bg-saudi-green transition-all shadow-lg shadow-corp-blue/20 hover:shadow-saudi-green/30 ${isArabic ? 'flex-row-reverse mr-auto' : ''}`}>
                {t.btn}
                <ArrowUpRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;

